import React, {
  FC,
  PropsWithChildren,
  useEffect,
  useRef,
  useContext,
  createContext,
  useState,
  useCallback,
} from 'react';
import * as styles from './ScrollableTimeline.module.scss';

type Props = PropsWithChildren;

type ScrollableTimelineContext = {
  progress: number;
  progressBetween: ([start, end]: [number, number]) => number;
};

const Context = createContext<ScrollableTimelineContext>({
  progress: 0,
  progressBetween: () => 0,
});
export const useScrollableTimeline = () => useContext(Context);

const ScrollableTimeline: FC<Props> = ({ children }) => {
  const [progress, setProgress] = useState(0);
  const scrollRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleScroll = () => {
    const scrollBox = scrollRef.current?.getBoundingClientRect();
    const containerBox = containerRef.current?.getBoundingClientRect();

    if (scrollBox && containerBox) {
      const progress = -scrollBox.top / (scrollBox.height - containerBox.height / 2);
      setProgress(progress);
    }
  };

  const progressBetween = useCallback(
    ([start, end]: [number, number]) => (progress - start) / (end - start),
    [progress]
  );

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <Context.Provider value={{ progress, progressBetween }}>
      <div ref={scrollRef} className={styles.scroller}>
        <div ref={containerRef} className={styles.container}>
          {children}
        </div>
      </div>
    </Context.Provider>
  );
};

export default ScrollableTimeline;
